'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { RefObject } from 'react';
import type { ProcessedMessage } from '../utils/message-processing';

const NEAR_BOTTOM_THRESHOLD_PX = 150;

export function countUnseenMessages({
  messages,
  lastSeenMessageId,
}: {
  messages: Pick<ProcessedMessage, 'message'>[];
  lastSeenMessageId: string | null;
}): number {
  const lastSeenIndex = messages.findIndex(
    ({ message }) => message.id === lastSeenMessageId,
  );

  if (lastSeenIndex === -1) {
    return 0;
  }

  return messages
    .slice(lastSeenIndex + 1)
    .filter(({ message }) => message.role !== 'user').length;
}

export function useUnseenChatMessages({
  messages,
  scrollElementRef,
}: {
  messages: ProcessedMessage[];
  scrollElementRef: RefObject<HTMLDivElement | null>;
}) {
  const [isNearBottom, setIsNearBottom] = useState(true);
  const [lastSeenMessageId, setLastSeenMessageId] = useState<string | null>(
    null,
  );
  const latestMessageId = messages.at(-1)?.message.id ?? null;

  const measureIsNearBottom = useCallback(() => {
    const scrollElement = scrollElementRef.current;

    if (!scrollElement) {
      return true;
    }

    return (
      scrollElement.scrollHeight -
        scrollElement.scrollTop -
        scrollElement.clientHeight <
      NEAR_BOTTOM_THRESHOLD_PX
    );
  }, [scrollElementRef]);

  useEffect(() => {
    const scrollElement = scrollElementRef.current;

    if (!scrollElement) {
      return;
    }

    const handleScroll = () => {
      setIsNearBottom(measureIsNearBottom());
    };

    handleScroll();
    scrollElement.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      scrollElement.removeEventListener('scroll', handleScroll);
    };
  }, [measureIsNearBottom, scrollElementRef]);

  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      if (measureIsNearBottom()) {
        setIsNearBottom(true);
      }
    });

    return () => {
      cancelAnimationFrame(frame);
    };
  }, [measureIsNearBottom, messages]);

  const isLastSeenMessagePresent = messages.some(
    ({ message }) => message.id === lastSeenMessageId,
  );

  useEffect(() => {
    if (isNearBottom || !isLastSeenMessagePresent) {
      setLastSeenMessageId(latestMessageId);
    }
  }, [isLastSeenMessagePresent, isNearBottom, latestMessageId]);

  const markAllSeen = useCallback(() => {
    setLastSeenMessageId(latestMessageId);
  }, [latestMessageId]);

  const unseenCount = useMemo(
    () =>
      isNearBottom ? 0 : countUnseenMessages({ messages, lastSeenMessageId }),
    [isNearBottom, lastSeenMessageId, messages],
  );

  return { unseenCount, markAllSeen };
}
